import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ResidentApplication as ResidentForm } from '../lib/types';
import { submitResident } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const initialForm: ResidentForm = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  zip: '',
  county: '',
  age: 'none',
  veteran: 'none',
  military_deployed: false,
  disability: false,
  medical: 'none',
  single_parent: false,
  widowed_recent: false,
  divorced_recent: false,
  code_enforcement: false,
  income: '' as ResidentForm['income'],   // forces selection — '' fails validation
  owns_other_property: false,
  referral_source: '',
  lot_size: '',
  frequency_requested: 'monthly',
  obstacles: '',
  gate_access: 'yes',
  pets: '',
  story: '',
  consent_release: false,
  consent_photos: false,
  consent_accuracy: false,
  honeypot: '',
};

const inputClass =
  'w-full bg-transparent border-b-2 border-forest/20 focus:border-terracotta focus:outline-none py-3 text-forest placeholder-forest/40 transition-colors';
const labelClass = 'block text-xs tracking-[0.2em] uppercase text-forest/70 mb-2';

const MAX_PHOTOS = 5;

type PhotoStatus = 'uploading' | 'done' | 'error';
interface PhotoState {
  id: string;
  preview: string;
  key?: string;
  status: PhotoStatus;
  error?: string;
}


export function ResidentApplication() {
  const { user } = useAuth();

  // Pre-fill name + email from the logged-in account if available.
  // Best-effort: applicants can edit if the lawn-care application is for
  // a family member or someone other than the account holder.
  const prefill: ResidentForm = (() => {
    if (!user) return initialForm;
    const nameParts = (user.name || '').trim().split(/\s+/);
    return {
      ...initialForm,
      email: user.email || '',
      first_name: nameParts[0] || '',
      last_name: nameParts.slice(1).join(' ') || '',
    };
  })();

  const [step, setStep] = useState(1);
  const [form, setForm] = useState<ResidentForm>(prefill);
  const [photos, setPhotos] = useState<PhotoState[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      for (const p of photos) URL.revokeObjectURL(p.preview);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function update<K extends keyof ResidentForm>(field: K, val: ResidentForm[K]) {
    setForm((prev) => ({ ...prev, [field]: val }));
    if (errors[field as string]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as string];
        return next;
      });
    }
  }

  const stepTitles = ['Contact', 'Your Situation', 'Property', 'Tell Us More', 'Review'];

  function validateStep(s: number): boolean {
    const e: Record<string, string> = {};
    if (s === 1) {
      if (!form.first_name.trim()) e.first_name = 'Required';
      if (!form.last_name.trim()) e.last_name = 'Required';
      if (!form.phone.trim()) e.phone = 'Required';
      else if (form.phone.replace(/\D/g, '').length < 10) e.phone = 'At least 10 digits';
      if (!form.address.trim()) e.address = 'Required';
      if (!form.city.trim()) e.city = 'Required';
      if (!form.zip.trim()) e.zip = 'Required';
      if (!form.county) e.county = 'Required';
      if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email';
    }
    if (s === 2) {
      if (!form.income) e.income = 'Required — pick the bracket that best fits your household.';
    }
    if (s === 3) {
      if (!form.lot_size) e.lot_size = 'Required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function next() {
    if (validateStep(step)) setStep(Math.min(step + 1, 5));
  }
  function back() {
    setStep(Math.max(step - 1, 1));
  }

  // ── Photo upload helpers ────────────────────────────────────────────────
  async function uploadOne(photoId: string, file: File) {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch('/api/upload', { method: 'POST', body: fd });
      const json = (await res.json()) as
        | { ok: true; data: { key: string } }
        | { ok: false; error: string };
      setPhotos((prev) =>
        prev.map((p) =>
          p.id === photoId
            ? json.ok
              ? { ...p, status: 'done', key: json.data.key }
              : { ...p, status: 'error', error: json.error }
            : p,
        ),
      );
    } catch (err) {
      setPhotos((prev) =>
        prev.map((p) =>
          p.id === photoId
            ? { ...p, status: 'error', error: err instanceof Error ? err.message : 'Network error' }
            : p,
        ),
      );
    }
  }

  function handlePhotoSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (picked.length === 0) return;
    const room = MAX_PHOTOS - photos.length;
    const queue = picked.slice(0, Math.max(0, room));
    for (const file of queue) {
      const id =
        (crypto as Crypto & { randomUUID?: () => string }).randomUUID?.() ||
        `${Date.now()}-${Math.random()}`;
      const preview = URL.createObjectURL(file);
      const initial: PhotoState = { id, preview, status: 'uploading' };
      setPhotos((prev) => [...prev, initial]);
      void uploadOne(id, file);
    }
  }

  function removePhoto(id: string) {
    setPhotos((prev) => {
      const found = prev.find((p) => p.id === id);
      if (found) URL.revokeObjectURL(found.preview);
      return prev.filter((p) => p.id !== id);
    });
  }

  const anyUploading = photos.some((p) => p.status === 'uploading');

  async function submit() {
    setSubmitError(null);
    if (!form.consent_release || !form.consent_accuracy) {
      setSubmitError('Please confirm the required consent items before submitting.');
      return;
    }
    if (anyUploading) {
      setSubmitError('Photos are still uploading — give it a moment.');
      return;
    }
    setSubmitting(true);
    const photo_keys = photos
      .filter((p): p is PhotoState & { key: string } => p.status === 'done' && !!p.key)
      .map((p) => p.key);
    const result = await submitResident({ ...form, photo_keys });
    setSubmitting(false);
    if (!result.ok) {
      setSubmitError(result.error || 'Something went wrong. Please try again or call us.');
      return;
    }
    navigate('/thank-you/resident');
  }

  const errClass = (field: string) => (errors[field] ? 'border-terracotta' : '');

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16">
      <div className="grid lg:grid-cols-12 gap-12">
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <div className="text-xs tracking-[0.25em] uppercase text-terracotta mb-3">Resident Application</div>
            <h1
              className="text-4xl text-forest mb-8 leading-tight font-display"
              style={{ fontVariationSettings: '"opsz" 144' }}
            >
              Tell us how we can help.
            </h1>
            <ol className="space-y-3 mb-8">
              {stepTitles.map((title, i) => (
                <li key={i} className="flex items-center gap-4">
                  <div
                    className={`w-8 h-8 flex items-center justify-center text-sm border font-display ${
                      step === i + 1
                        ? 'bg-terracotta text-cream border-terracotta'
                        : step > i + 1
                        ? 'bg-forest text-cream border-forest'
                        : 'border-forest/30 text-forest/50'
                    }`}
                  >
                    {step > i + 1 ? '✓' : i + 1}
                  </div>
                  <span className={`text-sm ${step === i + 1 ? 'text-forest' : 'text-forest/60'}`}>{title}</span>
                </li>
              ))}
            </ol>

            <div className="bg-forest text-cream p-6">
              <div className="text-xs tracking-[0.2em] uppercase text-sand mb-3">Your Privacy</div>
              <p className="text-sm leading-relaxed text-cream/85">
                Everything you share stays with our small review team. We do not
                publish names, addresses, or stories. If your application is
                approved, only the assigned Mowkin receives your address —
                and only after we confirm with you by phone first.
              </p>
            </div>
          </div>
        </aside>

        <div className="lg:col-span-8 bg-cream border border-forest/10 p-8 lg:p-12">
          {step === 1 && (
            <div>
              <div className="text-terracotta text-sm tracking-[0.2em] uppercase mb-2">Step 01</div>
              <h2 className="text-3xl text-forest mb-8 font-display">How can we reach you?</h2>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>First name *</label>
                  <input className={`${inputClass} ${errClass('first_name')}`} value={form.first_name} onChange={(e) => update('first_name', e.target.value)} placeholder="Maria" />
                  {errors.first_name && <div className="text-terracotta text-xs mt-1">{errors.first_name}</div>}
                </div>
                <div>
                  <label className={labelClass}>Last name *</label>
                  <input className={`${inputClass} ${errClass('last_name')}`} value={form.last_name} onChange={(e) => update('last_name', e.target.value)} placeholder="González" />
                  {errors.last_name && <div className="text-terracotta text-xs mt-1">{errors.last_name}</div>}
                </div>
                <div>
                  <label className={labelClass}>Phone *</label>
                  <input className={`${inputClass} ${errClass('phone')}`} value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="(361) 555-0123" inputMode="tel" />
                  {errors.phone && <div className="text-terracotta text-xs mt-1">{errors.phone}</div>}
                </div>
                <div>
                  <label className={labelClass}>Email (optional)</label>
                  <input className={`${inputClass} ${errClass('email')}`} value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="optional" inputMode="email" />
                  {errors.email && <div className="text-terracotta text-xs mt-1">{errors.email}</div>}
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>Street address *</label>
                  <input className={`${inputClass} ${errClass('address')}`} value={form.address} onChange={(e) => update('address', e.target.value)} placeholder="1234 Ocean Dr" />
                  {errors.address && <div className="text-terracotta text-xs mt-1">{errors.address}</div>}
                </div>
                <div>
                  <label className={labelClass}>City *</label>
                  <input className={`${inputClass} ${errClass('city')}`} value={form.city} onChange={(e) => update('city', e.target.value)} placeholder="Corpus Christi" />
                  {errors.city && <div className="text-terracotta text-xs mt-1">{errors.city}</div>}
                </div>
                <div>
                  <label className={labelClass}>ZIP *</label>
                  <input className={`${inputClass} ${errClass('zip')}`} value={form.zip} onChange={(e) => update('zip', e.target.value)} placeholder="78411" inputMode="numeric" />
                  {errors.zip && <div className="text-terracotta text-xs mt-1">{errors.zip}</div>}
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>County *</label>
                  <select className={`${inputClass} ${errClass('county')}`} value={form.county} onChange={(e) => update('county', e.target.value)}>
                    <option value="">Select your county</option>
                    <option value="nueces">Nueces</option>
                    <option value="san-patricio">San Patricio</option>
                    <option value="aransas">Aransas</option>
                    <option value="other">Other (we may not serve your area yet)</option>
                  </select>
                  {errors.county && <div className="text-terracotta text-xs mt-1">{errors.county}</div>}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="text-terracotta text-sm tracking-[0.2em] uppercase mb-2">Step 02</div>
              <h2 className="text-3xl text-forest mb-2 font-display">Your situation.</h2>
              <p className="text-forest/60 mb-8 text-sm">
                Please answer honestly. We cross-check applications during review. Nothing is shared outside our small team.
              </p>

              <div className="space-y-8">
                <div>
                  <label className={labelClass}>Age</label>
                  <div className="grid grid-cols-3 gap-3">
                    {(['none', '65-74', '75+'] as const).map((val) => {
                      const label = val === 'none' ? 'Under 65' : val === '65-74' ? '65–74' : '75 or older';
                      return (
                        <button key={val} type="button" onClick={() => update('age', val)} className={`py-3 border-2 text-sm transition-colors ${form.age === val ? 'border-terracotta bg-terracotta text-cream' : 'border-forest/20 text-forest hover:border-terracotta'}`}>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Veteran status</label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {([['none','Not a veteran'],['rated_30_69','Veteran, 30–69% disability rating'],['rated_70_plus','Veteran, 70%+ rating']] as const).map(([val, label]) => (
                      <button key={val} type="button" onClick={() => update('veteran', val)} className={`py-3 px-4 border-2 text-sm transition-colors text-left ${form.veteran === val ? 'border-terracotta bg-terracotta text-cream' : 'border-forest/20 text-forest hover:border-terracotta'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Medical situation</label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {([['none','None currently'],['recent','Recent (within 90 days)'],['severe','Severe / ongoing (chemo, post-op, hospice)']] as const).map(([val, label]) => (
                      <button key={val} type="button" onClick={() => update('medical', val)} className={`py-3 px-4 border-2 text-sm transition-colors text-left ${form.medical === val ? 'border-terracotta bg-terracotta text-cream' : 'border-forest/20 text-forest hover:border-terracotta'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Annual household income, before taxes *</label>
                  <p className="text-xs text-forest/50 mb-3">
                    Pick the range that best matches what your household brings in over a year.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {([['under_50_ami','Under $36,000'],['50_80_ami','$36,000 – $58,000'],['80_120_ami','$58,000 – $87,000'],['above_120_ami','Above $87,000']] as const).map(([val, label]) => (
                      <button key={val} type="button" onClick={() => update('income', val)} className={`py-3 px-4 border-2 text-sm transition-colors text-left ${form.income === val ? 'border-terracotta bg-terracotta text-cream' : 'border-forest/20 text-forest hover:border-terracotta'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                  {errors.income && <div className="text-terracotta text-xs mt-2">{errors.income}</div>}
                </div>

                <div>
                  <label className={labelClass}>Other circumstances (check all that apply)</label>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {([
                      ['military_deployed', 'Active military, currently deployed'],
                      ['disability', 'Living with disability (SSDI/SSI)'],
                      ['single_parent', 'Single parent of minor children'],
                      ['widowed_recent', 'Widowed within the last 12 months'],
                      ['divorced_recent', 'Divorced or separated within the last 12 months'],
                      ['code_enforcement', 'Active code enforcement notice'],
                    ] as const).map(([field, label]) => (
                      <label key={field} className={`flex items-start gap-3 p-3 border-2 cursor-pointer transition-colors ${form[field] ? 'border-terracotta bg-terracotta/5' : 'border-forest/15 hover:border-terracotta/50'}`}>
                        <input type="checkbox" checked={form[field]} onChange={(e) => update(field, e.target.checked)} className="mt-1 accent-terracotta" />
                        <span className="text-sm text-forest">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Referred by (optional)</label>
                  <p className="text-xs text-forest/50 mb-2">Caseworker, social worker, clergy, doctor, neighbor, etc.</p>
                  <input className={inputClass} value={form.referral_source} onChange={(e) => update('referral_source', e.target.value)} placeholder="Name and organization, if any" />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <div className="text-terracotta text-sm tracking-[0.2em] uppercase mb-2">Step 03</div>
              <h2 className="text-3xl text-forest mb-8 font-display">About your yard.</h2>
              <div className="space-y-6">
                <div>
                  <label className={labelClass}>Approximate lot size *</label>
                  <select className={`${inputClass} ${errClass('lot_size')}`} value={form.lot_size} onChange={(e) => update('lot_size', e.target.value)}>
                    <option value="">Select...</option>
                    <option value="under_5000">Under 5,000 sq ft (small)</option>
                    <option value="5000_10000">5,000 – 10,000 sq ft (typical city lot)</option>
                    <option value="10000_20000">10,000 – 20,000 sq ft (large)</option>
                    <option value="20000_plus">Over 20,000 sq ft (acreage)</option>
                    <option value="unknown">Not sure</option>
                  </select>
                  {errors.lot_size && <div className="text-terracotta text-xs mt-1">{errors.lot_size}</div>}
                </div>

                <div>
                  <label className={labelClass}>Property ownership</label>
                  <label className={`flex items-start gap-3 p-3 border-2 cursor-pointer transition-colors ${form.owns_other_property ? 'border-terracotta bg-terracotta/5' : 'border-forest/15 hover:border-terracotta/50'}`}>
                    <input type="checkbox" checked={form.owns_other_property} onChange={(e) => update('owns_other_property', e.target.checked)} className="mt-1 accent-terracotta" />
                    <span className="text-sm text-forest">
                      I own one or more <strong>additional</strong> residential properties besides this address.
                    </span>
                  </label>
                </div>

                <div>
                  <label className={labelClass}>Gate / access</label>
                  <div className="grid grid-cols-2 gap-3">
                    {([['yes','Yes — front access works'],['back','Backyard requires gate access']] as const).map(([val, label]) => (
                      <button key={val} type="button" onClick={() => update('gate_access', val)} className={`py-3 border-2 text-sm transition-colors ${form.gate_access === val ? 'border-terracotta bg-terracotta text-cream' : 'border-forest/20 text-forest hover:border-terracotta'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Pets in the yard?</label>
                  <input className={inputClass} value={form.pets} onChange={(e) => update('pets', e.target.value)} placeholder="e.g., 1 friendly dog, will be inside" />
                </div>
                <div>
                  <label className={labelClass}>Obstacles or notes</label>
                  <textarea className={inputClass + ' resize-none'} rows={3} value={form.obstacles} onChange={(e) => update('obstacles', e.target.value)} placeholder="Decorative rocks, sprinkler heads, sloped areas, etc." />
                </div>

                <div>
                  <label className={labelClass}>Photos of your yard (optional)</label>
                  <p className="text-xs text-forest/50 mb-3">
                    Up to {MAX_PHOTOS} photos. Helps our review team understand the scope. JPG, PNG, HEIC, or WEBP under 8 MB each.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {photos.map((p) => (
                      <div key={p.id} className="relative aspect-square border-2 border-forest/15 overflow-hidden bg-forest/5">
                        <img src={p.preview} alt="" className="w-full h-full object-cover" />
                        {p.status === 'uploading' && <div className="absolute inset-0 bg-forest/70 flex items-center justify-center text-cream text-xs tracking-[0.15em] uppercase">Uploading…</div>}
                        {p.status === 'error' && <div className="absolute inset-0 bg-terracotta/85 flex items-center justify-center text-cream text-xs p-2 text-center leading-snug">{p.error || 'Upload failed'}</div>}
                        <button type="button" onClick={() => removePhoto(p.id)} className="absolute top-1 right-1 w-7 h-7 bg-forest text-cream text-base leading-none flex items-center justify-center hover:bg-terracotta transition-colors" aria-label="Remove photo">×</button>
                      </div>
                    ))}
                    {photos.length < MAX_PHOTOS && (
                      <label className="aspect-square border-2 border-dashed border-forest/30 hover:border-terracotta hover:bg-terracotta/5 flex flex-col items-center justify-center cursor-pointer text-forest/70 hover:text-terracotta text-xs text-center p-3 transition-colors">
                        <div className="text-2xl mb-1 font-display">+</div>
                        <div className="tracking-[0.15em] uppercase">Add photo</div>
                        <input type="file" accept="image/*" multiple onChange={handlePhotoSelect} className="hidden" />
                      </label>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <div className="text-terracotta text-sm tracking-[0.2em] uppercase mb-2">Step 04</div>
              <h2 className="text-3xl text-forest mb-2 font-display">Anything else we should know?</h2>
              <p className="text-forest/60 mb-8 text-sm">
                Optional. Your story helps us prioritize cases that need a human touch.
              </p>
              <textarea
                className={inputClass + ' resize-none'}
                rows={8}
                value={form.story}
                onChange={(e) => update('story', e.target.value)}
                placeholder="A few sentences about your situation. What's been going on? What would it mean to have this taken off your plate?"
              />
            </div>
          )}

          {step === 5 && (
            <div>
              <div className="text-terracotta text-sm tracking-[0.2em] uppercase mb-2">Step 05</div>
              <h2 className="text-3xl text-forest mb-8 font-display">Review & submit.</h2>

              <div className="bg-cream-alt p-6 mb-8 border border-forest/10">
                <div className="text-xs tracking-[0.2em] uppercase text-forest/60 mb-3">Your Application Summary</div>
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div><span className="text-forest/60">Name:</span> {form.first_name} {form.last_name}</div>
                  <div><span className="text-forest/60">Phone:</span> {form.phone}</div>
                  <div><span className="text-forest/60">Address:</span> {form.address}, {form.city}</div>
                  <div><span className="text-forest/60">County:</span> {form.county || '—'}</div>
                  <div><span className="text-forest/60">Lot size:</span> {form.lot_size || '—'}</div>
                  <div className="sm:col-span-2"><span className="text-forest/60">Photos attached:</span> {photos.filter((p) => p.status === 'done').length}{anyUploading ? ' (some still uploading…)' : ''}</div>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                {([
                  ['consent_accuracy', 'I certify that the information in this application is true and accurate to the best of my knowledge.', true],
                  ['consent_release', 'I release MowKind and its provider partners from liability for incidental property damage (sprinkler heads, decorative items) during reasonable lawn care work.', true],
                  ['consent_photos', 'I consent to before-and-after photos of my yard being used in MowKind outreach (optional — no identifying info will be shared).', false],
                ] as const).map(([field, label, required]) => (
                  <label key={field} className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={form[field]} onChange={(e) => update(field, e.target.checked)} className="mt-1 accent-terracotta" />
                    <span className="text-sm text-forest">{label} {required && <span className="text-terracotta">*</span>}</span>
                  </label>
                ))}
              </div>

              {submitError && (
                <div className="bg-terracotta/10 border-l-4 border-terracotta p-4 text-terracotta text-sm mb-6">{submitError}</div>
              )}
            </div>
          )}

          <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.honeypot || ''} onChange={(e) => update('honeypot', e.target.value)} style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} aria-hidden="true" />

          <div className="flex justify-between mt-12 pt-8 border-t border-forest/10">
            <button onClick={back} disabled={step === 1} className={`text-sm tracking-[0.1em] uppercase px-6 py-3 transition-colors ${step === 1 ? 'text-forest/30 cursor-not-allowed' : 'text-forest hover:text-terracotta'}`}>← Back</button>
            {step < 5 ? (
              <button onClick={next} className="bg-forest text-cream px-8 py-3 text-sm tracking-[0.1em] uppercase hover:bg-terracotta transition-colors">Continue →</button>
            ) : (
              <button onClick={submit} disabled={submitting || anyUploading} className="bg-terracotta text-cream px-8 py-3 text-sm tracking-[0.1em] uppercase hover:bg-forest transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {submitting ? 'Submitting…' : anyUploading ? 'Photos uploading…' : 'Submit Application'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
